import { useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import {
    AlertTriangleIcon,
    CheckIcon,
    CopyIcon,
    DownloadIcon,
    HardDriveIcon,
    Loader2Icon,
    PackageIcon,
    PlayIcon,
    RefreshCwIcon,
    SearchIcon,
    ServerIcon,
    Trash2Icon,
    XIcon,
} from 'lucide-react';
import { useBackendApi } from '@/hooks/fetch';
import { useOpenConfirmDialog } from '@/hooks/dialogs';
import { txToast } from '@/components/TxToaster';
import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn, copyToClipboard } from '@/lib/utils';
import type {
    ArtifactInstallJob,
    ArtifactsListResp,
    ArtifactsStatusResp,
    InstalledArtifact,
    RemoteArtifact,
} from '@shared/vantaArtifactsTypes';
import {
    ReleaseTags,
    SupportCell,
    Tag,
    formatAgo,
    formatBytes,
    formatDate,
    formatDateTime,
} from './artifactsUtils';

/**
 * Artifacts (outil Vanta) — gestion des builds FXServer utilisés par le serveur de jeu.
 * API : /vanta/artifacts/<status|list|install|cancel|select|remove|cleanup|resync>
 */

type ActionResp = { success?: boolean; error?: string; removed?: string[] };
const NEXT_RESTART_NOTE = 'The change only applies the next time the FXServer is started (restart the server from txAdmin).';


//MARK: Stat card
function StatCard({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
    return (
        <div className="rounded-lg border bg-card p-4 flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                <span className="[&>svg]:size-4 opacity-75">{icon}</span>
                {label}
            </div>
            {children}
        </div>
    );
}

function BigValue({ children }: { children: React.ReactNode }) {
    return <div className="text-2xl font-semibold font-mono leading-tight flex items-center gap-2 flex-wrap">{children}</div>;
}

function SubLine({ children, className }: { children: React.ReactNode; className?: string }) {
    return <div className={cn('text-xs text-muted-foreground', className)}>{children}</div>;
}


//MARK: Commit
function CommitCell({ commit }: { commit: string | null }) {
    const ref = useRef<HTMLDivElement>(null);
    const [copied, setCopied] = useState(false);
    if (!commit) return <span className="text-muted-foreground">—</span>;
    const doCopy = () => {
        if (!ref.current) return;
        copyToClipboard(commit, ref.current).then((ok) => {
            if (ok === false) return;
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        }).catch(() => { });
    };
    return (
        <div ref={ref} className="inline-flex">
            <button
                type="button"
                onClick={doCopy}
                title={`Copy ${commit}`}
                className="inline-flex items-center gap-1.5 font-mono text-xs text-muted-foreground hover:text-foreground"
            >
                {commit.slice(0, 7)}
                {copied ? <CheckIcon className="size-3 text-success" /> : <CopyIcon className="size-3 opacity-60" />}
            </button>
        </div>
    );
}


//MARK: Install job
function JobBanner({ job, canManage, onCancel }: { job: ArtifactInstallJob; canManage: boolean; onCancel: () => void }) {
    const active = !job.finishedAt;
    const pct = job.bytesTotal ? Math.min(100, Math.floor((job.bytesDone / job.bytesTotal) * 100)) : null;
    const stageLabel: Record<ArtifactInstallJob['stage'], string> = {
        downloading: 'Downloading',
        extracting: 'Extracting',
        copying: 'Copying txAdmin (monitor)',
        finalizing: 'Finalizing',
        done: 'Installed',
        error: 'Failed',
    };
    return (
        <div className={cn(
            'rounded-lg border p-3 flex flex-col gap-2',
            job.stage === 'error' ? 'border-destructive/50 bg-destructive/5'
                : job.stage === 'done' ? 'border-success/50 bg-success/5' : 'bg-card',
        )}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 text-sm flex-wrap">
                    {active && <Loader2Icon className="size-4 animate-spin" />}
                    <span className="font-semibold">Build {job.build}</span>
                    <span className="text-muted-foreground">{stageLabel[job.stage]}</span>
                    {job.stage === 'downloading' && (
                        <span className="text-muted-foreground font-mono text-xs">
                            {formatBytes(job.bytesDone)} / {formatBytes(job.bytesTotal)}{pct !== null ? ` (${pct}%)` : ''}
                        </span>
                    )}
                    {job.error && <span className="text-destructive text-xs">{job.error}</span>}
                    {job.stage === 'done' && <span className="text-xs text-muted-foreground">Select it below to use it on the next start.</span>}
                </div>
                {active && canManage && (
                    <Button size="xs" variant="outline-destructive" onClick={onCancel}>
                        <XIcon className="size-3.5 mr-1" /> Cancel
                    </Button>
                )}
            </div>
            {active && (
                <div className="h-1.5 w-full rounded-sm bg-muted overflow-hidden">
                    <div
                        className={cn('h-full bg-primary transition-[width]', job.stage !== 'downloading' && 'animate-pulse')}
                        style={{ width: `${job.stage === 'downloading' ? (pct ?? 0) : 100}%` }}
                    />
                </div>
            )}
        </div>
    );
}


//MARK: Filter toggle
function FilterToggle({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'h-8 px-3 rounded-md border text-xs font-medium transition-colors',
                active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted',
            )}
        >
            {children}
        </button>
    );
}

const thClass = 'px-4 py-2 font-semibold';
const tdClass = 'px-4 py-2.5';


//MARK: Page
export default function ArtifactsPage() {
    const openConfirmDialog = useOpenConfirmDialog();
    const [search, setSearch] = useState('');
    const [onlyRecommended, setOnlyRecommended] = useState(false);
    const [onlyLatest, setOnlyLatest] = useState(false);
    const [onlySupported, setOnlySupported] = useState(false);
    const [showEol, setShowEol] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const statusApi = useBackendApi<ArtifactsStatusResp>({
        method: 'GET',
        path: '/vanta/artifacts/status',
        throwGenericErrors: true,
    });
    const listApi = useBackendApi<ArtifactsListResp>({
        method: 'GET',
        path: '/vanta/artifacts/list',
        throwGenericErrors: true,
    });
    const actionApi = useBackendApi<ActionResp>({
        method: 'POST',
        path: '/vanta/artifacts/:action',
    });

    const statusSwr = useSWR('/vanta/artifacts/status', async () => {
        const data = await statusApi({});
        if (!data) throw new Error('No data returned');
        return data;
    }, {
        refreshInterval: (data) => (data?.job && !data.job.finishedAt ? 1000 : 10_000),
    });
    const listSwr = useSWR('/vanta/artifacts/list', async () => {
        const data = await listApi({});
        if (!data) throw new Error('No data returned');
        return data;
    }, {
        revalidateOnFocus: false,
        refreshInterval: 15 * 60_000,
    });

    const status = statusSwr.data;
    const list = listSwr.data;
    const canManage = !!status?.canManage;
    const jobActive = !!status?.job && !status.job.finishedAt;
    const installedMap = useMemo(() => {
        return new Map((status?.installed ?? []).map((x) => [x.build, x]));
    }, [status]);

    //Filtering
    const eolCount = useMemo(() => list?.artifacts.filter((x) => x.support.status === 'eol').length ?? 0, [list]);
    const filtered = useMemo(() => {
        if (!list) return [];
        const q = search.trim().toLowerCase();
        return list.artifacts.filter((x) => {
            if (q && !String(x.build).includes(q) && !x.commit.toLowerCase().startsWith(q)) return false;
            if (onlyRecommended || onlyLatest) {
                const ok = (onlyRecommended && x.support.isRecommended) || (onlyLatest && x.support.isLatest);
                if (!ok) return false;
            }
            if (onlySupported && x.support.status !== 'supported') return false;
            if (!showEol && x.support.status === 'eol' && !q) return false;
            return true;
        });
    }, [list, search, onlyRecommended, onlyLatest, onlySupported, showEol]);

    //Actions
    const runAction = async (action: string, data: object, successMsg: string, loadingMsg: string) => {
        try {
            await actionApi({
                pathParams: { action },
                data,
                toastLoadingMessage: loadingMsg,
                genericHandler: { successMsg },
            });
        } catch (error) {
            //déjà affiché par le toast
        } finally {
            statusSwr.mutate();
        }
    };

    const doRefreshList = async () => {
        setIsRefreshing(true);
        try {
            const data = await listApi({ queryParams: { refresh: 1 } });
            if (data) listSwr.mutate(data, false);
            statusSwr.mutate();
        } catch (error) {
            txToast.error({ title: 'Failed to refresh the artifacts list', msg: (error as Error).message });
        } finally {
            setIsRefreshing(false);
        }
    };

    const askInstall = (a: RemoteArtifact) => {
        openConfirmDialog({
            title: `Install build ${a.build}?`,
            message: <div className="flex flex-col gap-2">
                <p>The build ({formatBytes(a.sizeBytes)} download) will be extracted into <code className="text-xs">{status?.storage.buildsPath}</code> and the current txAdmin will be copied into it.</p>
                <p className="text-muted-foreground text-sm">Installing does not change anything for the running server. You will need to select it afterwards.</p>
            </div>,
            actionLabel: 'Install',
            onConfirm: () => runAction('install', { build: a.build }, `Install of build ${a.build} started.`, `Starting install of build ${a.build}...`),
        });
    };

    const askSelect = (build: number | null) => {
        const label = build === null ? `the host artifact (build ${status?.host.build})` : `build ${build}`;
        openConfirmDialog({
            title: build === null ? 'Use the host artifact?' : `Select build ${build}?`,
            message: <div className="flex flex-col gap-2">
                <p>The game server will use {label} the next time it starts.</p>
                <p className="text-warning text-sm">{NEXT_RESTART_NOTE}</p>
            </div>,
            actionLabel: 'Select',
            confirmBtnVariant: 'warning',
            onConfirm: () => runAction('select', { build: build ?? 'host' }, `Selected ${label} for the next start.`, 'Saving selection...'),
        });
    };

    const askRemove = (x: InstalledArtifact) => {
        openConfirmDialog({
            title: `Remove build ${x.build}?`,
            message: `This deletes the folder of build ${x.build} (${formatBytes(x.sizeBytes)}). You can reinstall it later.`,
            actionLabel: 'Remove',
            confirmBtnVariant: 'destructive',
            onConfirm: () => runAction('remove', { build: x.build }, `Build ${x.build} removed.`, `Removing build ${x.build}...`),
        });
    };

    const askCleanup = () => {
        openConfirmDialog({
            title: 'Clean up builds?',
            message: `Keeps the ${status?.storage.keepCount ?? 3} most recent valid builds, plus the selected and running ones. Invalid builds and leftovers from failed installs are deleted.`,
            actionLabel: 'Clean up',
            confirmBtnVariant: 'destructive',
            onConfirm: () => runAction('cleanup', {}, 'Builds cleaned up.', 'Cleaning up builds...'),
        });
    };

    const doResync = (build: number) => runAction('resync', { build }, `txAdmin copy refreshed in build ${build}.`, 'Copying txAdmin...');
    const doCancel = () => runAction('cancel', {}, 'Install cancelled.', 'Cancelling...');

    const pendingChange = !!status && status.gameServer.running && (
        status.gameServer.build !== status.nextStart.build
        || status.gameServer.isHost !== status.nextStart.isHost
    );
    const selectedBuild = status?.nextStart.selected ?? null;
    const runningBuild = status?.gameServer.running ? status.gameServer.build : null;
    const osLabel = status ? (status.os === 'linux' ? 'Linux' : 'Windows') : '';

    return (
        <div className="w-full mb-10">
            <PageHeader icon={<PackageIcon />} title="Artifacts">
                <div className="flex items-center gap-2">
                    {list && <span className="text-xs text-muted-foreground">List updated {formatAgo(list.fetchedAt)}</span>}
                    <Button size="sm" variant="outline" onClick={doRefreshList} disabled={isRefreshing}>
                        <RefreshCwIcon className={cn('size-4 mr-1.5', isRefreshing && 'animate-spin')} /> Refresh
                    </Button>
                </div>
            </PageHeader>

            <div className="px-2 md:px-0 flex flex-col gap-4">
                {statusSwr.error && !status && (
                    <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
                        Failed to load the artifacts status: {(statusSwr.error as Error).message}
                    </div>
                )}

                {/* Cartes */}
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
                    <StatCard label="txAdmin host" icon={<ServerIcon />}>
                        <BigValue>
                            {status?.host.build ?? '—'}
                            {status && <ReleaseTags support={status.host.support} />}
                        </BigValue>
                        {status && <SupportCell support={status.host.support} compact />}
                        <SubLine>{status ? `${osLabel} · txAdmin v${status.host.txaVersion}` : 'loading...'}</SubLine>
                    </StatCard>

                    <StatCard label="Game server" icon={<PlayIcon />}>
                        <BigValue>
                            {status?.gameServer.running ? status.gameServer.build : <span className="text-muted-foreground">Stopped</span>}
                            {status?.gameServer.running && (status.gameServer.isHost
                                ? <Tag tone="muted">Host</Tag>
                                : <Tag tone="violet">Managed</Tag>)}
                        </BigValue>
                        <SubLine>
                            {status?.gameServer.running ? `Started ${formatAgo(status.gameServer.startedAt)}` : 'The FXServer is not running.'}
                        </SubLine>
                        {status?.gameServer.fallbackReason && (
                            <SubLine className="text-warning">{status.gameServer.fallbackReason}</SubLine>
                        )}
                    </StatCard>

                    <StatCard label="Next start" icon={<RefreshCwIcon />}>
                        <BigValue>
                            {status?.nextStart.build ?? '—'}
                            {status && (status.nextStart.isHost
                                ? <Tag tone="muted">Host artifact</Tag>
                                : <Tag tone="violet">Managed</Tag>)}
                        </BigValue>
                        {status?.nextStart.fallbackReason ? (
                            <SubLine className="text-destructive">{status.nextStart.fallbackReason}</SubLine>
                        ) : (
                            <SubLine>
                                {status?.nextStart.isHost ? 'Uses the artifact txAdmin runs from.' : 'Uses a build managed by txAdmin.'}
                            </SubLine>
                        )}
                        {status?.nextStart.monitorResync && (
                            <SubLine className="text-warning">The txAdmin copy in this build is outdated and will be refreshed on start.</SubLine>
                        )}
                    </StatCard>

                    <StatCard label="Storage" icon={<HardDriveIcon />}>
                        <BigValue>{status ? formatBytes(status.storage.installedBytes) : '—'}</BigValue>
                        <SubLine>
                            {status ? `${status.installed.length} build(s) installed · ${formatBytes(status.storage.freeBytes)} free` : 'loading...'}
                        </SubLine>
                        <SubLine>Clean up keeps the {status?.storage.keepCount ?? 3} most recent valid builds.</SubLine>
                    </StatCard>
                </div>

                {pendingChange && (
                    <div className="rounded-lg border border-warning/50 bg-warning/5 p-3 text-sm flex items-start gap-2">
                        <AlertTriangleIcon className="size-4 text-warning shrink-0 mt-0.5" />
                        <div>
                            The server is running {status.gameServer.isHost ? 'the host artifact' : 'managed build'} <b>{status.gameServer.build}</b> but {status.nextStart.isHost ? 'the host artifact' : 'managed build'} <b>{status.nextStart.build}</b> will be used on the next start.
                            <span className="text-muted-foreground"> {NEXT_RESTART_NOTE}</span>
                        </div>
                    </div>
                )}

                {status?.job && (
                    <JobBanner job={status.job} canManage={canManage} onCancel={doCancel} />
                )}

                {/* Installés */}
                <section className="rounded-lg border bg-card">
                    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b flex-wrap">
                        <div className="min-w-0">
                            <h2 className="font-semibold">Installed builds</h2>
                            <p className="text-xs text-muted-foreground break-all">
                                Stored in <code>{status?.storage.buildsPath ?? '...'}</code>. Each build gets a copy of this txAdmin so the Vanta tools stay active.
                            </p>
                        </div>
                        {canManage && (
                            <Button size="xs" variant="outline-muted" onClick={askCleanup} disabled={jobActive}>
                                <Trash2Icon className="size-3.5 mr-1" /> Clean up
                            </Button>
                        )}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                                    <th className={thClass}>Build</th>
                                    <th className={thClass}>Installed</th>
                                    <th className={thClass}>Size</th>
                                    <th className={thClass}>Support</th>
                                    <th className={thClass}>Monitor copy</th>
                                    <th className={cn(thClass, 'text-right')}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {status && (
                                    <tr className="border-b">
                                        <td className={tdClass}>
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="font-mono font-semibold">{status.host.build}</span>
                                                <Tag tone="muted">Host</Tag>
                                                <ReleaseTags support={status.host.support} />
                                                {runningBuild !== null && status.gameServer.isHost && <Tag tone="primary">Running</Tag>}
                                                {selectedBuild === null && <Tag tone="violet">Selected</Tag>}
                                            </div>
                                        </td>
                                        <td className={cn(tdClass, 'text-muted-foreground')} title={status.host.fxsPath}>Host artifact</td>
                                        <td className={cn(tdClass, 'text-muted-foreground')}>—</td>
                                        <td className={tdClass}><SupportCell support={status.host.support} /></td>
                                        <td className={cn(tdClass, 'text-muted-foreground text-xs')}>Not needed</td>
                                        <td className={cn(tdClass, 'text-right')}>
                                            {canManage && selectedBuild !== null && (
                                                <Button size="xs" variant="outline" onClick={() => askSelect(null)}>Select</Button>
                                            )}
                                        </td>
                                    </tr>
                                )}
                                {status?.installed.map((x) => {
                                    const isRunning = runningBuild === x.build && !status.gameServer.isHost;
                                    const isSelected = selectedBuild === x.build;
                                    return (
                                        <tr key={x.build} className="border-b last:border-0">
                                            <td className={tdClass}>
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span className="font-mono font-semibold">{x.build}</span>
                                                    {isRunning && <Tag tone="primary">Running</Tag>}
                                                    {isSelected && <Tag tone="violet">Selected</Tag>}
                                                    {!x.valid && <Tag tone="red" title={x.invalidReason}>Invalid</Tag>}
                                                    <ReleaseTags support={x.support} />
                                                </div>
                                                {!x.valid && <div className="text-xs text-destructive">Build {x.invalidReason}</div>}
                                            </td>
                                            <td className={cn(tdClass, 'whitespace-nowrap')}>{formatDateTime(x.installedAt)}</td>
                                            <td className={cn(tdClass, 'whitespace-nowrap font-mono text-xs')}>{formatBytes(x.sizeBytes)}</td>
                                            <td className={tdClass}><SupportCell support={x.support} /></td>
                                            <td className={tdClass}>
                                                {x.monitorCopy === 'ok' && <Tag tone="green">OK</Tag>}
                                                {x.monitorCopy === 'outdated' && <Tag tone="amber" title="Refreshed automatically on the next start of this build">Outdated</Tag>}
                                                {x.monitorCopy === 'missing' && <Tag tone="red">Missing</Tag>}
                                            </td>
                                            <td className={tdClass}>
                                                {canManage && (
                                                    <div className="flex justify-end gap-1.5">
                                                        {x.valid && x.monitorCopy !== 'ok' && !isRunning && (
                                                            <Button size="xs" variant="outline-warning" onClick={() => doResync(x.build)}>Sync txAdmin</Button>
                                                        )}
                                                        {x.valid && !isSelected && (
                                                            <Button size="xs" variant="outline" onClick={() => askSelect(x.build)}>Select</Button>
                                                        )}
                                                        {!isRunning && !isSelected && (
                                                            <Button size="xs" variant="ghost-destructive" onClick={() => askRemove(x)} title="Remove">
                                                                <Trash2Icon className="size-3.5" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {status && !status.installed.length && (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-4 text-center text-muted-foreground text-sm">
                                            No managed build installed yet. Install one from the list below.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Disponibles */}
                <section className="rounded-lg border bg-card">
                    <div className="flex flex-col gap-3 px-4 py-3 border-b">
                        <div>
                            <h2 className="font-semibold">Available builds</h2>
                            <p className="text-xs text-muted-foreground">
                                {osLabel} builds from Cfx.re (master branch). Support data from the Cfx changelog API, cached for 1 hour.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="relative w-full sm:w-64">
                                <SearchIcon className="size-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    className="h-8 pl-8 text-sm"
                                    placeholder="Search build or commit"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <FilterToggle active={onlyRecommended} onClick={() => setOnlyRecommended((v) => !v)}>Recommended</FilterToggle>
                            <FilterToggle active={onlyLatest} onClick={() => setOnlyLatest((v) => !v)}>Latest</FilterToggle>
                            <FilterToggle active={onlySupported} onClick={() => setOnlySupported((v) => !v)}>Supported only</FilterToggle>
                            <FilterToggle active={showEol} onClick={() => setShowEol((v) => !v)}>Show EOL ({eolCount})</FilterToggle>
                        </div>
                        {list?.warnings.map((w) => (
                            <div key={w} className="text-xs text-warning">{w}</div>
                        ))}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                                    <th className={thClass}>Build</th>
                                    <th className={thClass}>Published</th>
                                    <th className={thClass}>Commit</th>
                                    <th className={thClass}>Size</th>
                                    <th className={thClass}>Support</th>
                                    <th className={cn(thClass, 'text-right')}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!list && (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                                            {listSwr.error
                                                ? `Failed to load the list: ${(listSwr.error as Error).message}`
                                                : <><Loader2Icon className="size-4 inline animate-spin mr-2" />Loading builds...</>}
                                        </td>
                                    </tr>
                                )}
                                {list && !filtered.length && (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No build matches the filters.</td>
                                    </tr>
                                )}
                                {filtered.map((a) => {
                                    const inst = installedMap.get(a.build);
                                    const isHost = status?.host.build === a.build;
                                    const isJobBuild = jobActive && status?.job?.build === a.build;
                                    return (
                                        <tr key={a.build} className={cn('border-b last:border-0', a.support.status === 'eol' && 'opacity-60')}>
                                            <td className={tdClass}>
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span className="font-mono font-semibold">{a.build}</span>
                                                    <ReleaseTags support={a.support} />
                                                    {isHost && <Tag tone="muted">Host</Tag>}
                                                </div>
                                            </td>
                                            <td className={cn(tdClass, 'whitespace-nowrap')} title={a.publishedAtIsEstimate ? 'Estimated from the support policy' : undefined}>
                                                {a.publishedAtIsEstimate && '~'}{formatDate(a.publishedAt)}
                                            </td>
                                            <td className={tdClass}><CommitCell commit={a.commit} /></td>
                                            <td className={cn(tdClass, 'whitespace-nowrap font-mono text-xs')}>{formatBytes(a.sizeBytes)}</td>
                                            <td className={tdClass}><SupportCell support={a.support} /></td>
                                            <td className={cn(tdClass, 'text-right whitespace-nowrap')}>
                                                {isJobBuild ? (
                                                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                                                        <Loader2Icon className="size-3.5 animate-spin" /> Installing
                                                    </span>
                                                ) : inst?.valid ? (
                                                    <span className="text-xs text-success inline-flex items-center gap-1"><CheckIcon className="size-3.5" /> Installed</span>
                                                ) : canManage ? (
                                                    <Button size="xs" variant="outline" disabled={jobActive} onClick={() => askInstall(a)}>
                                                        <DownloadIcon className="size-3.5 mr-1" /> {inst ? 'Reinstall' : 'Install'}
                                                    </Button>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>

                {!canManage && status && (
                    <p className="text-xs text-muted-foreground">Managing builds requires the "All Permissions" permission.</p>
                )}
            </div>
        </div>
    );
}
